import { useState, type FormEvent } from 'react';
import { Check, Eye, EyeOff, TriangleAlert } from 'lucide-react';
import { PORTAL_PASSWORD_RULE_MESSAGE, portalPasswordValid } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { setPortalPassword } from '../api';
import { PhoneFrame } from '../frame/PhoneFrame';
import styles from '../frame/Phone.module.css';
import { clearPortalMagicToken } from '../magic';
import type { PortalActivation, PortalMagicView } from '../types';
import local from './Activation.module.css';
import { ReadyScreen } from './ReadyScreen';

/** The four segments of the strength bar (Portal 08): length ≥ 10, a digit, upper and lower case, a special sign. */
export function passwordStrength(password: string): number {
  let score = 0;
  if (password.length >= 10) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[a-zäöüß]/.test(password) && /[A-ZÄÖÜ]/.test(password)) score += 1;
  if (/[^A-Za-z0-9äöüßÄÖÜ]/.test(password)) score += 1;
  return score;
}

export function StrengthBar({ password }: { password: string }) {
  const score = passwordStrength(password);
  return (
    <div className={local.strength} aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <span key={index} className={clsx(local.segment, index < score && local.segmentOn)} />
      ))}
    </div>
  );
}

export function PasswordInput({
  id,
  value,
  onChange,
  invalid,
  autoComplete,
  describedBy,
  autoFocus,
  toggle = true,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean | undefined;
  autoComplete: string;
  describedBy?: string | undefined;
  autoFocus?: boolean | undefined;
  /** The eye toggle of Portal 08; the login (11/13) draws none. */
  toggle?: boolean | undefined;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className={clsx(styles.control, invalid && styles.controlInvalid)}>
      <input
        id={id}
        className={clsx(styles.input, !visible && styles.secret)}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        autoFocus={autoFocus}
      />
      {toggle ? (
        <button
          type="button"
          className={styles.eye}
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Passwort verbergen' : 'Passwort anzeigen'}
          aria-pressed={visible}
        >
          {visible ? <EyeOff size={17} strokeWidth={1.5} /> : <Eye size={17} strokeWidth={1.5} />}
        </button>
      ) : null}
    </div>
  );
}

export function FieldError({ id, text }: { id: string; text: string }) {
  return (
    <div id={id} className={styles.fieldError} role="alert">
      <TriangleAlert size={14} strokeWidth={1.75} aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

/**
 * Mandantenportal 08 „Passwort setzen" (magic link state `password`): with the pill „Selbstauskunft eingegangen"
 * when the customer arrives from the submitted form; the invitation/reset variant reads „Legen Sie Ihr Passwort
 * fest" (own). Success → 09.
 */
export function PasswordScreen({
  token,
  view,
  fromSubmit,
}: {
  token: string;
  view: PortalMagicView;
  fromSubmit: boolean;
}) {
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<PortalActivation | null>(null);

  if (result) return <ReadyScreen result={result} />;

  const valid = portalPasswordValid(password);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!valid) next['password'] = PORTAL_PASSWORD_RULE_MESSAGE;
    else if (password !== repeat) next['passwordRepeat'] = 'Die Passwörter stimmen nicht überein.';
    setErrors(next);
    setBanner(null);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    try {
      const activation = await setPortalPassword(token, password, repeat);
      clearPortalMagicToken();
      setResult(activation);
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) setErrors(cause.fields);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <PhoneFrame title="Mandantenportal">
      <form className={local.passwordForm} onSubmit={(event) => void submit(event)} noValidate>
        <div className={local.passwordHead}>
          {fromSubmit ? (
            <span className={local.receivedPill}>
              <Check size={13} strokeWidth={2.5} aria-hidden="true" />
              Selbstauskunft eingegangen
            </span>
          ) : null}
          <h1 className={clsx(local.passwordTitle, fromSubmit && local.afterPill)}>
            {fromSubmit ? 'Ein Schritt fehlt noch: Ihr Passwort' : 'Legen Sie Ihr Passwort fest'}
          </h1>
          <p className={local.passwordText}>
            Damit legen wir Ihr Mandantenportal an. Dort sehen Sie Ihr Finanzierungsvolumen und Ihren Weg zum
            Eigentum.
          </p>
        </div>
        <div className={`${styles.fields} ${local.passwordFields}`}>
          {banner ? (
            <div className={styles.banner} role="alert">
              <TriangleAlert size={17} strokeWidth={1.75} aria-hidden="true" />
              <div className={styles.bannerTitle}>{banner}</div>
            </div>
          ) : null}
          <div>
            <span className={styles.label} id="portal-email-label">
              Ihre E-Mail
            </span>
            <div className={styles.readonly} aria-labelledby="portal-email-label">
              {view.email}
            </div>
          </div>
          <div>
            <label className={styles.label} htmlFor="portal-password">
              Passwort
            </label>
            <PasswordInput
              id="portal-password"
              value={password}
              onChange={setPassword}
              invalid={Boolean(errors['password'])}
              autoComplete="new-password"
              describedBy="portal-password-hint"
              autoFocus
            />
            <StrengthBar password={password} />
            {errors['password'] ? (
              <FieldError id="portal-password-hint" text={errors['password']} />
            ) : (
              <div id="portal-password-hint" className={styles.hint}>
                {PORTAL_PASSWORD_RULE_MESSAGE}
                {valid ? ' Erfüllt.' : ''}
              </div>
            )}
          </div>
          <div>
            <label className={styles.label} htmlFor="portal-password-repeat">
              Passwort wiederholen
            </label>
            <PasswordInput
              id="portal-password-repeat"
              value={repeat}
              onChange={setRepeat}
              invalid={Boolean(errors['passwordRepeat'])}
              autoComplete="new-password"
              describedBy={errors['passwordRepeat'] ? 'portal-password-repeat-error' : undefined}
            />
            {errors['passwordRepeat'] ? (
              <FieldError id="portal-password-repeat-error" text={errors['passwordRepeat']} />
            ) : null}
          </div>
        </div>
        <div className={`${styles.footer} ${local.passwordFooter}`}>
          <button type="submit" className={`pressable ${styles.primary}`} disabled={pending}>
            Portal anlegen
          </button>
        </div>
      </form>
    </PhoneFrame>
  );
}
